/**
 * v1 CSV import flow (SPEC-AUDITOR-CSV-IMPORT.md section 7): pick a file ->
 * preview (pure dry-run, nothing written) -> apply -> final counts.
 *
 * Re-importing an already-imported month is the NORMAL case, so the preview
 * stays calm: the verdict line leads, detail tables appear only when there is
 * something to show, and an all-unchanged file is a one-line non-event.
 */
import { useEffect, useRef, useState } from 'react';

import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

import type { ImportPreviewResponse, ImportRequest, ImportSummary } from '../../api/index.ts';
import { useAuth } from '../../auth/AuthContext.tsx';
import { QuietButton } from '../../components/QuietButton.tsx';
import { actionError } from '../../components/actionError.ts';
import { useFullScreenDialog } from '../../hooks/useFullScreenDialog.ts';
import { palette } from '../../theme.ts';
import { ImportPreviewTables } from './ImportPreviewTables.tsx';
import { importVerdict, isQuietNonEvent, sentenceCase } from './importSummary.ts';

/** Route cap from spec section 2 -- checked here too, so an obvious mispick
 *  never spends a round trip. */
const MAX_IMPORT_BYTES = 1024 * 1024;

const VISUALLY_HIDDEN: React.CSSProperties = {
  clipPath: 'inset(50%)',
  height: 1,
  width: 1,
  overflow: 'hidden',
  position: 'absolute',
  whiteSpace: 'nowrap',
  bottom: 0,
  left: 0,
};

/** FileReader, not Blob#text() (SonarQube S7756, accepted): jsdom 26 ships no
 *  Blob.prototype.text, so Blob#text() would make this path untestable -- and
 *  dev=prod forbids branching on the environment. */
function readFileText(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(typeof reader.result === 'string' ? reader.result : '');
    reader.onerror = () => reject(new Error('file_read_failed'));
    reader.readAsText(file);
  });
}

interface ImportProblem {
  message: string;
  /** Always says where the data stands, then what to do next. */
  hint: string;
}

export interface ImportDialogProps {
  open: boolean;
  onClose: () => void;
  /** Fires once after a successful apply so the page reloads its ledger. */
  onApplied: () => void;
}

export function ImportDialog({ open, onClose, onApplied }: Readonly<ImportDialogProps>): React.JSX.Element {
  const { api } = useAuth();
  const fullScreen = useFullScreenDialog();

  const [request, setRequest] = useState<ImportRequest | null>(null);
  const [preview, setPreview] = useState<ImportPreviewResponse | null>(null);
  const [applied, setApplied] = useState<ImportSummary | null>(null);
  const [problem, setProblem] = useState<ImportProblem | null>(null);
  const [busy, setBusy] = useState(false);
  const applyingRef = useRef(false);

  useEffect(() => {
    if (!open) return;
    setRequest(null);
    setPreview(null);
    setApplied(null);
    setProblem(null);
    setBusy(false);
    applyingRef.current = false;
  }, [open]);

  async function chooseFile(input: HTMLInputElement): Promise<void> {
    const file = input.files?.[0];
    // Clearing lets the same file be re-picked after a failure.
    input.value = '';
    if (!file) return;
    if (file.size > MAX_IMPORT_BYTES) {
      setProblem({
        message: `${file.name} is larger than 1 MB.`,
        hint: 'A monthly v1 export is a few tens of kilobytes. Check you picked the right file.',
      });
      return;
    }
    setProblem(null);
    setPreview(null);
    setApplied(null);
    setBusy(true);
    try {
      const content = await readFileText(file);
      const body: ImportRequest = { filename: file.name, content };
      const result = await api.reports.importPreview(body);
      setRequest(body);
      setPreview(result);
    } catch (e) {
      setProblem({
        message: sentenceCase(actionError(e)),
        hint: 'Nothing was imported. Fix the file and choose it again.',
      });
    } finally {
      setBusy(false);
    }
  }

  async function applyImport(): Promise<void> {
    if (request === null || applyingRef.current) return;
    applyingRef.current = true;
    setBusy(true);
    setProblem(null);
    try {
      const result = await api.reports.importApply(request);
      setApplied(result.summary);
      onApplied();
    } catch (e) {
      setProblem({ message: sentenceCase(actionError(e)), hint: 'Nothing was imported. You can apply again.' });
    } finally {
      setBusy(false);
      applyingRef.current = false;
    }
  }

  const showPicker = applied === null && preview === null;

  const filePicker = (
    <Button component="label" variant="outlined" disabled={busy} sx={{ alignSelf: 'flex-start' }}>
      {preview === null ? 'Choose CSV file' : 'Choose a different file'}
      <input
        type="file"
        accept=".csv,text/csv"
        aria-label="Import CSV file"
        // Out of the tab order: the label-Button is the focusable control and
        // carries the focus ring; the input would be a blank second stop.
        tabIndex={-1}
        style={VISUALLY_HIDDEN}
        onChange={(e) => void chooseFile(e.currentTarget)}
      />
    </Button>
  );

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>Import v1 CSV</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {showPicker && (
            <Stack spacing={1}>
              <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                Choose a monthly export from the v1 auditor. Nothing is written until you apply the preview.
              </Typography>
              {filePicker}
            </Stack>
          )}

          {problem !== null && (
            <Alert role="alert" severity="error">
              {problem.message}
              <Typography variant="body2" sx={{ mt: 0.5 }}>
                {problem.hint}
              </Typography>
            </Alert>
          )}

          {busy && (
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
              <CircularProgress size={18} />
              <Typography variant="body2">{preview === null ? 'Reading the file...' : 'Applying...'}</Typography>
            </Stack>
          )}

          {applied !== null && (
            <Stack spacing={1}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                Import complete
              </Typography>
              <Typography>{importVerdict(applied)}</Typography>
              <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                {request?.filename}
              </Typography>
            </Stack>
          )}

          {/* The verdict leads the preview (spec section 7); re-picking sits after it. */}
          {applied === null && preview !== null && (
            <Stack spacing={2.5}>
              <Stack spacing={0.5}>
                <Typography sx={{ fontWeight: 700 }}>{importVerdict(preview.summary, preview.rows)}</Typography>
                <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                  {request?.filename}
                </Typography>
              </Stack>
              {isQuietNonEvent(preview.summary) && (
                <Typography variant="body2" sx={{ color: palette.textSecondary }}>
                  Every row in this file is already in v2, unchanged.
                </Typography>
              )}
              <ImportPreviewTables rows={preview.rows} />
              {filePicker}
            </Stack>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <QuietButton onClick={onClose} disabled={busy}>
          {applied === null ? 'Cancel' : 'Close'}
        </QuietButton>
        {applied === null && (
          <Button onClick={() => void applyImport()} disabled={busy || preview === null}>
            Apply import
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default ImportDialog;
